// @greenlight-spec: 10-security-compliance.md
// @greenlight-spec: 28-it-dashboard-ui.md
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { notifyUnauthenticated } from '@/lib/auth';
import { isTransientQueryError } from '@/lib/polling';

/**
 * ts-rest throws non-2xx responses (including 401) into React Query's error
 * channel; a resolved `{ status: 401, ... }` is also recognized for defense in
 * depth. `auth.session_required` / `auth.unauthenticated` are the only meanings
 * of a 401 across MCP/REST/dashboard (docs/16); 403 (`auth.forbidden`) is
 * authorization, not a missing session, and must not bounce the user to sign-in.
 */
export function isUnauthorizedResponse(data: unknown): boolean {
  return (
    typeof data === 'object' &&
    data !== null &&
    'status' in data &&
    (data as { status?: unknown }).status === 401
  );
}

export function makeQueryClient(): QueryClient {
  const flagIfUnauthorized = (data: unknown, error: unknown): void => {
    if (isUnauthorizedResponse(data) || isUnauthorizedResponse(error)) {
      notifyUnauthenticated();
    }
  };
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (!isTransientQueryError(error)) {
          // setQueryData(undefined) leaves the previous snapshot intact.
          query.setState({ data: undefined, dataUpdatedAt: 0 });
        }
      },
      onSettled: flagIfUnauthorized,
    }),
    mutationCache: new MutationCache({ onSettled: flagIfUnauthorized }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => failureCount < 1 && isTransientQueryError(error),
      },
    },
  });
}
